import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  const { t } = useI18n();
  const all = [{ label: t('home'), to: '/' }, ...items];
  return (
    <nav aria-label="Breadcrumb" className="mb-4 overflow-x-auto scrollbar-none">
      <ol className="flex items-center gap-1 whitespace-nowrap text-sm text-ink-muted">
        {all.map((it, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="size-3.5" aria-hidden />}
            {it.to && i < all.length - 1 ? <Link to={it.to} className="hover:text-brand-800">{it.label}</Link> : <span aria-current={i === all.length - 1 ? 'page' : undefined} className="max-w-[16rem] truncate font-medium text-ink">{it.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PageHeader({ title, subtitle, eyebrow, children, className }: { title: ReactNode; subtitle?: ReactNode; eyebrow?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <header className={cn('relative overflow-hidden border-b border-line bg-white', className)}>
      <div className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-brand-100/60 blur-3xl" aria-hidden />
      <div className="container relative py-8 md:py-12">
        {eyebrow}
        <h1 className="font-display text-3xl font-bold text-ink md:text-4xl">{title}</h1>
        {subtitle && <p className="mt-2 max-w-2xl text-base text-ink-muted md:text-lg">{subtitle}</p>}
        {children && <div className="mt-6">{children}</div>}
      </div>
    </header>
  );
}

export function SectionHeading({ title, description, action, align = 'left', tone = 'default' }: { title: ReactNode; description?: ReactNode; action?: ReactNode; align?: 'left' | 'center'; tone?: 'default' | 'light' }) {
  return (
    <div className={cn('mb-6 flex flex-wrap items-end justify-between gap-3', align === 'center' && 'flex-col items-center text-center')}>
      <div className={cn(align === 'center' && 'mx-auto max-w-2xl')}>
        <h2 className={cn('font-display text-2xl font-bold tracking-tight md:text-[28px]', tone === 'light' ? 'text-white' : 'text-ink')}>{title}</h2>
        {description && <p className={cn('mt-1.5 text-base', tone === 'light' ? 'text-white/80' : 'text-ink-muted')}>{description}</p>}
      </div>
      {action}
    </div>
  );
}
